// app/ has no slice, so no model/ to hold hooks — colocate the hook next to
// the component per spec §4.3 (docs/superpowers/specs/2026-07-30-separate-ui-logic-design.md).
import { notification } from 'antd';
import type { ArgsProps } from 'antd/es/notification/interface';

/**
 * Creates the app-wide antd notification instance used to raise toasts. Configures stacking beyond three notifications, a five-second duration and bottom-left placement, returning the api with its context holder. Success, info and warning toasts default to role="status" (a non-urgent live region antd can render nameless) rather than antd's default role="alert", which per the ARIA spec takes its accessible name from an author-supplied label only and never from the visible message/description text.
 *
 * @returns The notification api and the context holder element to render.
 */
export const useNotificationProvider = () => {
  const [notificationApi, contextHolder] = notification.useNotification({
    stack: { threshold: 3 },
    duration: 20,
    placement: 'bottomLeft',
  });

  /**
   * Marks a toast as a polite status message instead of antd's default alert.
   * Returns a copy of the toast args with role set to status, letting an explicit role in args win.
   *
   * @param args - The antd notification arguments for one toast.
   * @returns The notification arguments carrying role status.
   */
  const asStatus = (args: ArgsProps) => ({ role: 'status' as const, ...args });

  return {
    notificationApi: {
      ...notificationApi,
      success: (args: ArgsProps) => notificationApi.success(asStatus(args)),
      info: (args: ArgsProps) => notificationApi.info(asStatus(args)),
      warning: (args: ArgsProps) => notificationApi.warning(asStatus(args)),
    },
    contextHolder,
  };
};
